import { Box, Grid, GridItem, Heading, Skeleton, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { BorrowingBaseReportGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  useGetBorrowingBaseReportQuery,
  useGetBorrowingBaseReportsQuery,
} from '~/packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryWithValueAndReference } from '~/packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryWithValueAndReference';
import {
  BorrowingBaseSummarySections,
  getTemplateColumnsForAccordionRow,
} from '~/packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Select } from '~/packages/common-frontend/components/Select';

export type DigitalMonthlyReportBorrowingBaseSection =
  | 'all'
  | 'receivables'
  | 'cash'
  | 'finalBorrowingBaseReport';
type DigitalMonthlyReportBorrowingBaseBodyProps = {
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  currentBorrowingBaseReport: BorrowingBaseReportGQLType;
  selectedReferenceBorrowingBaseReport: BorrowingBaseReportGQLType | undefined;
  section: DigitalMonthlyReportBorrowingBaseSection;
};
const DigitalMonthlyReportBorrowingBaseBody = ({
  creditAgreement,
  currentBorrowingBaseReport,
  selectedReferenceBorrowingBaseReport,
  section,
}: DigitalMonthlyReportBorrowingBaseBodyProps) => {
  const requestedSections = useMemo(() => {
    switch (section) {
      case 'all':
        return [
          BorrowingBaseSummarySections.receivables,
          BorrowingBaseSummarySections.cash,
          BorrowingBaseSummarySections.finalBorrowingBaseReport,
        ];
      default:
        return [BorrowingBaseSummarySections[section]];
    }
  }, [section]);

  return (
    <BorrowingBaseSummaryWithValueAndReference
      borrowingBaseReports={{
        minuend: currentBorrowingBaseReport,
        reference: selectedReferenceBorrowingBaseReport,
      }}
      creditAgreement={creditAgreement}
      sections={requestedSections}
    />
  );
};

type DigitalMonthlyReportBorrowingBaseProps = {
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  borrowingBaseReportId: string;
  reportingDate: Util.FullDateStr;
  section: DigitalMonthlyReportBorrowingBaseSection;
};
export const DigitalMonthlyReportBorrowingBase = ({
  creditAgreement,
  borrowingBaseReportId,
  reportingDate,
  section,
}: DigitalMonthlyReportBorrowingBaseProps) => {
  const [
    selectedReferenceBorrowingBaseReport,
    setSelectedReferenceBorrowingBaseReport,
  ] = useState<BorrowingBaseReportGQLType | undefined>(undefined);

  const startReportingDate = commonDate.addMonths(reportingDate, -3);

  const {
    data: borrowingBaseReportData,
    loading: isBorrowingBaseReportLoading,
  } = useGetBorrowingBaseReportQuery({ variables: { borrowingBaseReportId } });

  const {
    data: borrowingBaseReportsData,
    loading: isBorrowingBaseReportsLoading,
  } = useGetBorrowingBaseReportsQuery({
    variables: {
      creditFacilityId: creditAgreement.creditFacilityId,
      startReportingDate,
      endReportingDate: reportingDate,
    },
  });

  const selectOptions = useMemo(() => {
    const allBorrowingBaseReports =
      borrowingBaseReportsData?.borrowingBaseReports;

    if (allBorrowingBaseReports?.length) {
      const orderedBorrowingBaseReports = _.orderBy(
        borrowingBaseReportsData?.borrowingBaseReports,
        ['reportingDate', 'fileVersion'],
        ['desc', 'desc'],
      );

      const borrowingBaseReportsForReportingDate = _.keyBy(
        orderedBorrowingBaseReports,
        'reportingDate',
      );

      return _.map(
        borrowingBaseReportsForReportingDate,
        (borrowingBaseReport) => {
          return {
            value: borrowingBaseReport,
            label: commonDate.fullDateStrToHumanDateString(
              borrowingBaseReport.reportingDate,
            ),
          };
        },
      );
    }

    return [];
  }, [borrowingBaseReportsData?.borrowingBaseReports]);

  if (
    !borrowingBaseReportData ||
    isBorrowingBaseReportLoading ||
    !borrowingBaseReportsData ||
    isBorrowingBaseReportsLoading
  ) {
    return <Skeleton h="600px" />;
  }

  return (
    <Box
      backgroundColor="white"
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      padding="40px"
    >
      {section === 'all' ? (
        <Heading as="h2" fontSize="24px" mb="40px">
          Borrowing Base
        </Heading>
      ) : null}

      <Grid
        templateColumns={getTemplateColumnsForAccordionRow({
          showComparisonAndDifferenceColumns: false,
          showValueAndReferenceColumnsOnly: true,
        })}
        gridColumnGap="16px"
        p="8px 16px"
      >
        <GridItem colSpan={2} />
        <GridItem colSpan={1} px="16px">
          <Box>
            <Text fontWeight={600} mb="16px">
              Reference Reporting Date
            </Text>
            <Select
              value={selectedReferenceBorrowingBaseReport}
              onChange={setSelectedReferenceBorrowingBaseReport}
              options={selectOptions}
              placeholderLabel="Select"
            />
          </Box>
        </GridItem>
        <GridItem colSpan={1}>
          <Box>
            <Text fontWeight={600} mb="16px">
              Current Reporting Date
            </Text>

            <Text
              backgroundColor={colors.aurora[50]}
              fontWeight={600}
              borderRadius="8px"
              p="8px 16px"
            >
              {commonDate.fullDateStrToHumanDateString(
                borrowingBaseReportData.borrowingBaseReport.reportingDate,
              )}
            </Text>
          </Box>
        </GridItem>
      </Grid>
      <DigitalMonthlyReportBorrowingBaseBody
        creditAgreement={creditAgreement}
        section={section}
        currentBorrowingBaseReport={borrowingBaseReportData.borrowingBaseReport}
        selectedReferenceBorrowingBaseReport={
          selectedReferenceBorrowingBaseReport
        }
      />
    </Box>
  );
};
